import { useState, useEffect, useRef, useCallback } from 'react';
import { Building2, ChevronDown, X, RefreshCw, Check, Search, Plus } from 'lucide-react';
import { dataEngineApi } from '../../services/dataEngineApi';
import { chatTheme as th } from '../../theme/chat';

/**
 * Shared company selector with configurable behavior.
 *
 * Props:
 *   selected          - array of selected realm objects [{rf_id, company_name, ...}]
 *   onSelect          - callback(realm) when a company is added
 *   onRemove          - callback(rfId) when a company is removed
 *   multiSelect       - allow selecting multiple (default: true)
 *   showSync          - show sync controls + progress (default: false)
 *   direction         - 'up' | 'down' (default: 'up')
 *   trigger           - 'button' | 'icon' | 'pill' (default: 'button')
 *   maxHeight         - dropdown scrollable area max height (default: '16rem')
 *   className         - additional classes on root
 */
export function CompanySelector({
  selected = [],
  onSelect,
  onRemove,
  multiSelect = true,
  showSync = false,
  direction = 'up',
  trigger = 'button',
  maxHeight = '16rem',
  className = '',
}) {
  const [realms, setRealms] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [syncingIds, setSyncingIds] = useState(new Set());
  const [syncProgress, setSyncProgress] = useState({});
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const dd = th.dropdown; // shorthand

  // ── Data fetching ──
  const fetchRealms = useCallback(() => {
    dataEngineApi.getRealms()
      .then(data => setRealms(data.data || data.realms || []))
      .catch(() => setRealms([]));
  }, []);

  useEffect(() => { fetchRealms(); }, [fetchRealms]);

  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Sync ──
  const handleSync = async (e, realm) => {
    e.stopPropagation();
    try {
      setSyncingIds(prev => new Set([...prev, realm.rf_id]));
      const startTime = Date.now();
      setSyncProgress(prev => ({ ...prev, [realm.rf_id]: { elapsed: 0, status: 'Starting...', records: 0 } }));

      const result = await dataEngineApi.triggerSync(realm.rf_id);
      if (!result.data?.job_id) { cleanupSync(realm.rf_id); return; }

      const poll = setInterval(async () => {
        try {
          const elapsed = Math.round((Date.now() - startTime) / 1000);
          const status = await dataEngineApi.getPipelineLog(result.data.job_id);
          const s = status.data?.status;
          setSyncProgress(prev => ({
            ...prev,
            [realm.rf_id]: {
              elapsed,
              status: s === 'RUNNING' ? `Syncing... ${elapsed}s` : s === 'COMPLETED' ? 'Done' : s === 'FAILED' ? 'Failed' : s,
              records: status.data?.execute_summary?.total_inserted || 0,
              error: status.data?.error_message,
              duration: status.data?.duration_ms ? `${(status.data.duration_ms / 1000).toFixed(1)}s` : null,
            }
          }));
          if (['COMPLETED', 'COMPLETED_WITH_WARNINGS', 'FAILED'].includes(s)) {
            clearInterval(poll);
            setTimeout(() => { cleanupSync(realm.rf_id); fetchRealms(); }, 3000);
          }
        } catch { clearInterval(poll); cleanupSync(realm.rf_id); }
      }, 2000);
    } catch { cleanupSync(realm.rf_id); }
  };

  const cleanupSync = (rfId) => {
    setSyncingIds(prev => { const n = new Set(prev); n.delete(rfId); return n; });
    setSyncProgress(prev => { const n = { ...prev }; delete n[rfId]; return n; });
  };

  // ── Helpers ──
  const fuzzyMatch = (text, query) => {
    const tl = text.toLowerCase(), ql = query.toLowerCase();
    let qi = 0;
    for (let i = 0; i < tl.length && qi < ql.length; i++) { if (tl[i] === ql[qi]) qi++; }
    return qi === ql.length;
  };

  const getSyncDot = (lastSyncedAt) => {
    if (!lastSyncedAt) return 'bg-red-500';
    const h = (Date.now() - new Date(lastSyncedAt)) / 3600000;
    if (h < 24) return 'bg-green-500';
    if (h < 168) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getSyncLabel = (lastSyncedAt) => {
    if (!lastSyncedAt) return 'Not synced';
    const h = (Date.now() - new Date(lastSyncedAt)) / 3600000;
    if (h < 1) return `${Math.round(h * 60)}m ago`;
    if (h < 24) return `${Math.round(h)}h ago`;
    if (h < 168) return `${Math.round(h / 24)}d ago`;
    return 'Stale';
  };

  const toggleOpen = () => {
    setOpen(!open);
    setSearch('');
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const handleSelect = (realm) => {
    onSelect(realm);
    if (!multiSelect) setOpen(false);
  };

  const available = realms
    .filter(r => !selected.find(s => s.rf_id === r.rf_id))
    .filter(r => !search || fuzzyMatch(r.company_name || r.rf_id, search));

  const companyLabel = selected.length > 0
    ? selected.length === 1 ? (selected[0].company_name || selected[0].rf_id) : `${selected.length} companies`
    : 'Select Company';

  // ── Progress badge ──
  const ProgressBadge = ({ rfId }) => {
    const progress = syncProgress[rfId];
    if (!progress) return null;
    const cls = progress.status === 'Done' ? dd.syncDone : progress.status === 'Failed' ? dd.syncFail : dd.syncRun;
    return (
      <div className={`mx-2 mb-1 px-2 py-1 rounded text-xs ${cls}`}>
        {progress.status}
        {progress.records > 0 && ` · ${progress.records.toLocaleString()} records`}
        {progress.duration && ` · ${progress.duration}`}
        {progress.error && <div className="text-red-500 mt-0.5 truncate">{progress.error}</div>}
      </div>
    );
  };

  // ── Dropdown content ──
  const dropdownContent = (
    <div className={`absolute ${direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'} left-0 w-80 ${dd.bg} border ${dd.border} rounded-lg ${dd.shadow} z-50 overflow-hidden`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-3 py-2 border-b ${dd.headerBorder}`}>
        <span className={`text-xs uppercase tracking-wider ${dd.headerText}`}>Companies</span>
        <button onClick={fetchRealms} className={`text-xs flex items-center gap-1 ${dd.headerText} hover:opacity-80`}>
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* Search */}
      <div className={`flex items-center gap-2 px-3 py-2 border-b ${dd.headerBorder}`}>
        <Search className={`h-3.5 w-3.5 flex-shrink-0 ${dd.headerText}`} />
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search companies..."
          className={`flex-1 text-sm outline-none bg-transparent ${dd.searchText} ${dd.searchPlaceholder}`}
        />
      </div>

      {/* Selected */}
      {selected.length > 0 && (
        <div className={`p-2 border-b ${dd.headerBorder}`}>
          <div className={`text-xs mb-1 px-1 uppercase tracking-wider ${dd.headerText}`}>Selected</div>
          {selected.map(c => {
            const isSyncing = syncingIds.has(c.rf_id);
            return (
              <div key={c.rf_id}>
                <div className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded ${dd.selectedItemText} ${dd.itemHover}`}>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getSyncDot(c.last_synced_at)}`} />
                  <span className="flex-1 truncate">{c.company_name || c.rf_id}</span>
                  <span className={`text-xs ${dd.muted}`}>{getSyncLabel(c.last_synced_at)}</span>
                  {showSync && (
                    <button onClick={(e) => handleSync(e, c)} disabled={isSyncing}
                      className={`p-0.5 ${dd.headerText} hover:opacity-80`} title="Sync now">
                      <RefreshCw className={`h-3 w-3 ${isSyncing ? 'animate-spin' : ''}`} />
                    </button>
                  )}
                  <button onClick={() => onRemove(c.rf_id)} className={`p-0.5 ${dd.pillRemove}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
                {showSync && <ProgressBadge rfId={c.rf_id} />}
              </div>
            );
          })}
        </div>
      )}

      {/* Available */}
      <div className="overflow-y-auto" style={{ maxHeight }}>
        <div className={`text-xs px-3 pt-2 pb-1 uppercase tracking-wider ${dd.headerText}`}>Available</div>
        {available.length > 0 ? available.map(realm => {
          const isSyncing = syncingIds.has(realm.rf_id);
          return (
            <div key={realm.rf_id}>
              <div
                className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${dd.itemText} ${dd.itemHover}`}
                onClick={() => handleSelect(realm)}
              >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getSyncDot(realm.last_synced_at)}`} />
                <span className="flex-1 truncate">{realm.company_name || realm.rf_id}</span>
                <span className={`text-xs ${dd.muted}`}>{(realm.row_count || 0).toLocaleString()}</span>
                {showSync && (
                  <button onClick={(e) => handleSync(e, realm)} disabled={isSyncing}
                    className={`p-0.5 ${dd.headerText} hover:opacity-80`} title="Sync now">
                    {isSyncing
                      ? <RefreshCw className="h-3 w-3 animate-spin" />
                      : realm.row_count > 0
                        ? <Check className="h-3 w-3 text-emerald-500/50" />
                        : <RefreshCw className="h-3 w-3" />
                    }
                  </button>
                )}
              </div>
              {showSync && <ProgressBadge rfId={realm.rf_id} />}
            </div>
          );
        }) : (
          <div className={`px-3 py-2 text-xs ${dd.muted}`}>{search ? 'No matches' : 'No more companies'}</div>
        )}
      </div>
    </div>
  );

  // ── Trigger variants ──
  if (trigger === 'pill') {
    return (
      <div className={`flex items-center gap-1.5 flex-wrap ${className}`}>
        {selected.map(c => (
          <span key={c.rf_id} className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-full text-[11px] max-w-[160px] ${th.pill.bg} ${th.pill.border} ${th.pill.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getSyncDot(c.last_synced_at)}`} />
            <span className="truncate">{c.company_name || c.rf_id}</span>
            <button onClick={() => onRemove(c.rf_id)} className={`ml-0.5 flex-shrink-0 ${th.pill.remove}`}>
              <X size={10} />
            </button>
          </span>
        ))}
        <div className="relative" ref={dropdownRef}>
          <button onClick={toggleOpen}
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs border border-dashed rounded-full ${dd.addBtn}`}>
            <Plus className="h-3 w-3" /> Add
          </button>
          {open && dropdownContent}
        </div>
      </div>
    );
  }

  if (trigger === 'icon') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button onClick={toggleOpen} className={`p-1 rounded transition-colors ${dd.triggerIcon}`} title="Select company">
          <Building2 size={14} />
        </button>
        {open && dropdownContent}
      </div>
    );
  }

  // Default: 'button' trigger
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button onClick={toggleOpen} className={`flex items-center gap-1.5 text-sm transition-colors ${dd.triggerBtn}`}>
        <Building2 className="h-3.5 w-3.5" />
        <span>{companyLabel}</span>
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && dropdownContent}
    </div>
  );
}
